import type {
  SelectedWorkspaceVersion,
  WorkspaceDocument,
  WorkspaceDocumentRevision,
} from "@/models/workspace/Workspace";

/**
 * What the viewer needs to say which revision is on screen and whether it is
 * the current one, rather than leaving that to be inferred from the version
 * history sidebar (LAR-423).
 */
export type WorkspaceDocumentVersionView = {
  /** Revision number being rendered. */
  version: number;
  /** The revision the workspace points at — what "aktuel" means for a document. */
  currentRevision: WorkspaceDocumentRevision;
  totalVersions: number;
  isCurrent: boolean;
};

/** The viewer selection for one revision of a document. */
export const toSelectedWorkspaceVersion = (
  workspaceDocument: WorkspaceDocument,
  revision: WorkspaceDocumentRevision
): SelectedWorkspaceVersion => ({
  blobName: revision.blobId,
  name: workspaceDocument.name,
  updatedAt: revision.updatedAt,
  revisionVersion: revision.revisionVersion,
  size: revision.size,
  documentType: workspaceDocument.documentType,
});

/** The revision `activeRevision` names, or the highest one when it is unset. */
const resolveCurrentRevision = (
  workspaceDocument: WorkspaceDocument
): WorkspaceDocumentRevision | undefined => {
  const active = workspaceDocument.revisions.find(
    (candidate) =>
      candidate.revisionVersion === workspaceDocument.activeRevision
  );
  if (active) return active;

  return workspaceDocument.revisions.reduce<
    WorkspaceDocumentRevision | undefined
  >(
    (highest, candidate) =>
      !highest || candidate.revisionVersion > highest.revisionVersion
        ? candidate
        : highest,
    undefined
  );
};

/**
 * Describes the revision on screen.
 *
 * The selection is persisted across reloads and can outlive the document it
 * was made on, so it is matched against this document's revisions by blob and
 * by nothing else: both viewers fetch `selectedVersion.blobName`, so that blob
 * is the only thing that identifies what is on screen. A selection matching no
 * revision yields no description at all — falling back to the revision number
 * (or to the active revision) would put a green "Aktuel version" over content
 * this cannot identify.
 */
export const resolveWorkspaceDocumentVersionView = (
  workspaceDocument: WorkspaceDocument | null | undefined,
  selectedVersion: SelectedWorkspaceVersion | null | undefined
): WorkspaceDocumentVersionView | null => {
  if (!workspaceDocument?.revisions.length) return null;

  const currentRevision = resolveCurrentRevision(workspaceDocument);
  if (!currentRevision) return null;

  const viewedRevision = selectedVersion
    ? workspaceDocument.revisions.find(
        (revision) => revision.blobId === selectedVersion.blobName
      )
    : currentRevision;

  if (!viewedRevision) return null;

  return {
    version: viewedRevision.revisionVersion,
    currentRevision,
    totalVersions: workspaceDocument.revisions.length,
    isCurrent:
      viewedRevision.revisionVersion === currentRevision.revisionVersion,
  };
};
